/**
 * GroupSeatManagement Use Case
 * 그룹 좌석 관리 상태 및 로직
 */
import { useState, useEffect, useCallback } from 'react';
import {
  SeatGroup,
  AvailableSeat,
  GroupFormData,
  GroupType,
} from '../models/GroupSeatManagement';
import { groupSeatManagementRepository } from '../repositories/GroupSeatManagementRepository';
import { groupSeatManagementService } from '../services/GroupSeatManagementService';

export const useGroupSeatManagement = () => {
  const [groups, setGroups] = useState<SeatGroup[]>([]);
  const [availableSeats, setAvailableSeats] = useState<AvailableSeat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<SeatGroup | null>(null);
  const [selectedSeats, setSelectedSeats] = useState<number[]>([]);

  const [formData, setFormData] = useState<GroupFormData>({
    name: '',
    description: '',
    group_type: 'study_room' as GroupType,
    capacity: 4,
    hourly_rate: 6000,
    seat_numbers: [],
  });

  /**
   * 그룹 목록 로드
   */
  const loadGroups = useCallback(async () => {
    try {
      const data = await groupSeatManagementRepository.getGroups();
      setGroups(data);
      setError(null);
    } catch (error) {
      console.error('그룹 로드 실패:', error);
      setError('그룹 목록을 불러오는데 실패했습니다.');
    }
  }, []);

  /**
   * 사용 가능한 좌석 로드
   */
  const loadAvailableSeats = useCallback(async () => {
    try {
      const data = await groupSeatManagementRepository.getAvailableSeats();
      setAvailableSeats(data);
      setError(null);
    } catch (error) {
      console.error('좌석 로드 실패:', error);
      setError('사용 가능한 좌석을 불러오는데 실패했습니다.');
    }
  }, []);

  /**
   * 초기 데이터 로드
   */
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([loadGroups(), loadAvailableSeats()]);
      setLoading(false);
    };
    loadData();
  }, [loadGroups, loadAvailableSeats]);

  /**
   * 좌석 선택 토글
   */
  const toggleSeatSelection = useCallback((seatNumber: number) => {
    setSelectedSeats((prev) =>
      prev.includes(seatNumber)
        ? prev.filter((s) => s !== seatNumber)
        : [...prev, seatNumber]
    );
  }, []);

  /**
   * 모달 열기 (신규 생성)
   */
  const openCreateModal = useCallback(() => {
    setEditingGroup(null);
    setFormData({
      name: '',
      description: '',
      group_type: 'study_room',
      capacity: 4,
      hourly_rate: 6000,
      seat_numbers: [],
    });
    setSelectedSeats([]);
    setShowModal(true);
  }, []);

  /**
   * 모달 열기 (수정)
   */
  const openEditModal = useCallback((group: SeatGroup) => {
    setEditingGroup(group);
    setFormData({
      name: group.name,
      description: group.description || '',
      group_type: group.group_type,
      capacity: group.capacity,
      hourly_rate: group.hourly_rate,
      seat_numbers: group.seats,
    });
    setSelectedSeats(group.seats);
    setShowModal(true);
  }, []);

  /**
   * 모달 닫기
   */
  const closeModal = useCallback(() => {
    setShowModal(false);
    setEditingGroup(null);
    setSelectedSeats([]);
  }, []);

  /**
   * 그룹 저장 (생성/수정)
   */
  const handleSaveGroup = useCallback(async () => {
    const validation = groupSeatManagementService.validateGroupForm(
      formData.name,
      formData.capacity,
      formData.hourly_rate,
      selectedSeats
    );

    if (!validation.isValid) {
      alert(validation.error);
      return;
    }

    try {
      const dataToSave = {
        ...formData,
        seat_numbers: selectedSeats,
      };

      if (editingGroup) {
        // 수정
        await groupSeatManagementRepository.updateGroup(editingGroup.id, dataToSave);
        alert('그룹이 수정되었습니다.');
      } else {
        // 생성
        await groupSeatManagementRepository.createGroup(dataToSave);
        alert('그룹이 생성되었습니다.');
      }

      await loadGroups();
      await loadAvailableSeats();
      closeModal();
    } catch (error) {
      console.error('그룹 저장 실패:', error);
      alert(
        editingGroup ? '그룹 수정에 실패했습니다.' : '그룹 생성에 실패했습니다.'
      );
    }
  }, [formData, selectedSeats, editingGroup, loadGroups, loadAvailableSeats, closeModal]);

  /**
   * 그룹 삭제
   */
  const handleDeleteGroup = useCallback(
    async (groupId: string) => {
      if (!window.confirm('정말 이 그룹을 삭제하시겠습니까?')) {
        return;
      }

      try {
        await groupSeatManagementRepository.deleteGroup(groupId);
        alert('그룹이 삭제되었습니다.');
        await loadGroups();
        await loadAvailableSeats();
      } catch (error) {
        console.error('그룹 삭제 실패:', error);
        alert('그룹 삭제에 실패했습니다.');
      }
    },
    [loadGroups, loadAvailableSeats]
  );

  /**
   * 그룹 활성화/비활성화 토글
   */
  const handleToggleActive = useCallback(
    async (groupId: string, currentActive: boolean) => {
      try {
        await groupSeatManagementRepository.toggleGroupActive(groupId, !currentActive);
        await loadGroups();
      } catch (error) {
        console.error('그룹 상태 변경 실패:', error);
        alert('그룹 상태 변경에 실패했습니다.');
      }
    },
    [loadGroups]
  );

  return {
    // 상태
    groups,
    availableSeats,
    loading,
    error,
    showModal,
    editingGroup,
    formData,
    setFormData,
    selectedSeats,

    // 액션
    toggleSeatSelection,
    openCreateModal,
    openEditModal,
    closeModal,
    handleSaveGroup,
    handleDeleteGroup,
    handleToggleActive,
  };
};
